import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
    HelpCircle,
    MessageCircle,
    LayoutDashboard,
    LogOut,
    Palette,
    PiggyBank,
    ScrollText,
    Shield,
    ShieldCheck,
    ShoppingBag,
    Sparkles,
    TrendingUp,
    Wrench,
} from "lucide-react";
import { usePartnerAuth } from "@/context/PartnerAuthContext";
import OverviewPanel from "@/components/partner/OverviewPanel";
import { StudioAlertsCard } from "@/components/partner/StudioAlertsCard";
import { ProductPhotosCard } from "@/components/partner/ProductPhotosCard";
import QuestionsPanel from "@/components/partner/QuestionsPanel";
import { GiftNotePricesCard } from "@/components/partner/GiftNotePricesCard";
import { GiftNotesCard } from "@/components/partner/GiftNotesCard";
import AffiliateHub from "@/components/partner/AffiliateHub";
import FAQPanel from "@/components/partner/FAQPanel";
import AmendmentBanner from "@/components/partner/AmendmentBanner";
import StoreEditor from "@/components/partner/StoreEditor";
import BrandingEditor from "@/components/partner/BrandingEditor";
import ChangesPanel from "@/components/partner/ChangesPanel";
import RequestsPanel from "@/components/partner/RequestsPanel";
import PartnerAIStudio from "@/components/partner/PartnerAIStudio";
import PricingPanel from "@/components/partner/PricingPanel";
import LegalNameBackfillModal from "@/components/partner/LegalNameBackfillModal";

const TABS = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "pricing", label: "Pricing", icon: PiggyBank },
    { id: "affiliate", label: "Affiliate Hub", icon: TrendingUp },
    { id: "store", label: "Store", icon: ShoppingBag },
    { id: "branding", label: "Branding", icon: Palette },
    { id: "ai", label: "AI Studio", icon: Sparkles },
    { id: "changes", label: "Changes", icon: ScrollText },
    { id: "requests", label: "Requests", icon: Wrench },
    { id: "faq", label: "FAQ", icon: HelpCircle },
    { id: "questions", label: "Questions", icon: MessageCircle },
];

export default function PartnerDashboard() {
    const { status, partner, signout } = usePartnerAuth();
    const [tab, setTab] = useState("overview");

    if (status === "checking") return null;
    if (status === "guest") {
        return (
            <Navigate
                to={`/partner/auth?next=${encodeURIComponent("/partner/dashboard")}`}
                replace
            />
        );
    }
    if (!partner?.agreement_accepted) {
        return (
            <Navigate
                to={`/partner/agreement?next=${encodeURIComponent("/partner/dashboard")}`}
                replace
            />
        );
    }

    return (
        <div
            className="bg-forest text-cream min-h-screen py-10 md:py-14 px-5 md:px-10 relative overflow-hidden"
            data-testid="partner-dashboard-page"
        >
            <div
                className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-15"
                style={{ background: "var(--ig-gold)" }}
                aria-hidden
            />
            <div className="grain absolute inset-0" />
            <div className="relative max-w-6xl mx-auto">
                {/* Header — ASMR Creator identity */}
                <div className="flex flex-wrap items-start justify-between gap-6">
                    <div>
                        <span className="divider-rule text-gold">
                            <Sparkles size={12} /> ASMR Creator Studio · Island Gold
                        </span>
                        <h1 className="font-serif text-5xl md:text-6xl text-cream mt-5 leading-[1.02]">
                            Welcome,{" "}
                            <span className="italic text-gold">
                                {partner.email.split("@")[0]}
                            </span>
                        </h1>
                        <p className="mt-3 text-cream/60 font-mono text-sm">
                            {partner.email}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {partner.is_owner && (
                            <Link
                                to="/partner/owner"
                                data-testid="partner-dashboard-owner-admin-link"
                                className="btn-pill btn-gold inline-flex items-center gap-2 text-xs"
                            >
                                <Shield size={13} /> Owner admin
                            </Link>
                        )}
                        <button
                            type="button"
                            onClick={signout}
                            data-testid="partner-dashboard-signout"
                            className="btn-pill btn-ghost-light inline-flex items-center gap-2 text-xs"
                        >
                            <LogOut size={13} /> Sign out
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <nav
                    className="mt-10 flex gap-1.5 overflow-x-auto no-scrollbar pb-2 border-b border-cream/10"
                    role="tablist"
                    aria-label="Dashboard sections"
                >
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.id;
                        return (
                            <button
                                key={t.id}
                                role="tab"
                                type="button"
                                aria-selected={active}
                                onClick={() => setTab(t.id)}
                                data-testid={`partner-tab-${t.id}`}
                                className={`shrink-0 inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-[0.68rem] tracking-[0.28em] uppercase transition-colors ${
                                    active
                                        ? "bg-gold text-forest-deep"
                                        : "text-cream/60 hover:text-cream hover:bg-cream/5"
                                }`}
                            >
                                <Icon size={13} />
                                {t.label}
                                {t.soon && (
                                    <span className="text-[0.55rem] tracking-[0.15em] bg-cream/10 text-cream/70 px-1.5 py-0.5 rounded-full">
                                        soon
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </nav>

                {/* Panel */}
                <div className="mt-10">
                    <AmendmentBanner />
                    <LegalNameBackfillModal />
                    {tab === "overview" && (
                        <>
                            <StudioAlertsCard />
                            <ProductPhotosCard />
                            <GiftNotesCard />
                            <OverviewPanel partner={partner} />
                        </>
                    )}
                    {tab === "pricing" && (
                        <>
                            <PricingPanel />
                            <GiftNotePricesCard />
                        </>
                    )}
                    {tab === "affiliate" && <AffiliateHub />}
                    {tab === "store" && <StoreEditor />}
                    {tab === "branding" && <BrandingEditor />}
                    {tab === "ai" && <PartnerAIStudio />}
                    {tab === "changes" && <ChangesPanel />}
                    {tab === "requests" && <RequestsPanel />}
                    {tab === "faq" && <FAQPanel />}
                    {tab === "questions" && <QuestionsPanel />}
                </div>
            </div>
        </div>
    );
}

